/**
 * EventDocumentWorkspace — DCC-01
 * Platform document management for events.
 *
 * Access model: Tenant + Event + Thread + Document + User Capabilities + Publication State
 * Every action is capability-gated (document.read / upload / approve / delete / visibility.manage)
 *
 * Can render as:
 *   - Standalone page: /crm/events/:eid/documents
 *   - Embedded tab: embedded=true (from EventMasterWorkspace > Documents tab)
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Upload, Download, Eye, CheckCircle2, XCircle,
  Clock, AlertTriangle, Loader2, FileText, Image, Plus, ChevronDown,
  Shield, Lock, History, Trash2, Filter, Search, RefreshCw,
  FileBadge, FolderOpen, FileCheck, Archive,
} from "lucide-react";
import { documentApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// ── Design tokens ─────────────────────────────────────────────────────────────
const LC = {
  DRAFT:        { label: "Draft",       cls: "bg-slate-100 text-slate-600 border-slate-200" },
  UNDER_REVIEW: { label: "In Review",   cls: "bg-amber-100 text-amber-700 border-amber-200" },
  APPROVED:     { label: "Approved",    cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  REJECTED:     { label: "Rejected",    cls: "bg-red-100 text-red-700 border-red-200" },
  ARCHIVED:     { label: "Archived",    cls: "bg-slate-100 text-slate-400 border-slate-200" },
};

const VC = {
  CUSTOMER_VISIBLE: { label: "Customer Visible", cls: "bg-blue-100 text-blue-700", icon: Eye },
  INTERNAL:         { label: "Internal",         cls: "bg-slate-100 text-slate-600", icon: Lock },
  FINANCE_ONLY:     { label: "Finance Only",     cls: "bg-purple-100 text-purple-700", icon: Shield },
  RESTRICTED:       { label: "Restricted",       cls: "bg-red-100 text-red-700", icon: Shield },
};

const DOCTYPE_ICONS: Record<string, any> = {
  IMAGE: Image,
  DESIGN: Image,
  FLOOR_PLAN: Image,
};

const ALLOWED_EXTENSIONS = ["pdf", "docx", "xlsx", "pptx", "csv", "txt", "jpg", "jpeg", "png", "webp"];
const ALLOWED_MIMES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/csv", "text/plain",
  "image/jpeg", "image/png", "image/webp",
];

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function LifecycleBadge({ status }) {
  const cfg = LC[status] || { label: status, cls: "bg-slate-100 text-slate-500" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function VisibilityBadge({ vis }) {
  const cfg = VC[vis] || { label: vis, cls: "bg-slate-100 text-slate-500", icon: Lock };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
}

// ── Upload Modal ──────────────────────────────────────────────────────────────
function UploadModal({ eid, threads, onClose, onCreated }) {
  const [step, setStep] = useState("meta"); // meta | file | uploading | done
  const [docId, setDocId] = useState(null);
  const [meta, setMeta] = useState({
    title: "", document_type: "OTHER", thread_id: "",
    visibility_class: "", description: "", tags: "",
  });
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const fileRef = useRef();

  const upd = (k, v) => setMeta(m => ({ ...m, [k]: v }));

  const handleMetaSubmit = async () => {
    if (!meta.title.trim()) { setError("Title is required"); return; }
    if (!meta.thread_id) { setError("Select a parent thread"); return; }
    setError("");
    try {
      const res = await documentApi.createDocument(eid, {
        title: meta.title.trim(),
        document_type: meta.document_type,
        thread_id: meta.thread_id,
        visibility_class: meta.visibility_class || undefined,
        description: meta.description || undefined,
        tags: meta.tags ? meta.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      });
      setDocId(res.data.id);
      setStep("file");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create document record");
    }
  };

  const handleFileSelect = (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError(`File type .${ext} is not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`);
      return;
    }
    const limitMB = ["jpg","jpeg","png","webp"].includes(ext) ? 20 : 50;
    if (f.size > limitMB * 1024 * 1024) {
      setError(`File exceeds ${limitMB} MB limit`);
      return;
    }
    setError("");
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file || !docId) return;
    setStep("uploading");
    setProgress(0);
    try {
      await documentApi.uploadFile(eid, docId, file, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      setStep("done");
      toast.success("Document uploaded and scanned successfully");
      onCreated();
    } catch (e) {
      const msg = e.response?.data?.detail || "Upload failed";
      setError(msg);
      setStep("file");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="upload-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="p-6 border-b border-slate-100">
          <h3 className="font-bold text-slate-900 text-lg">
            {step === "done" ? "Document Uploaded" : step === "uploading" ? "Uploading…" : step === "file" ? "Select File" : "New Document"}
          </h3>
        </div>
        <div className="p-6 space-y-4">
          {step === "meta" && (
            <>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Title <span className="text-red-400">*</span></Label>
                <Input value={meta.title} onChange={e => upd("title", e.target.value)} placeholder="Event Brief Q4 2026" data-testid="doc-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Document Type</Label>
                  <select value={meta.document_type} onChange={e => upd("document_type", e.target.value)}
                    className="w-full rounded-lg border border-slate-200 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="doc-type-select">
                    {["PROPOSAL","CONTRACT","FLOOR_PLAN","DESIGN","PERMIT","BRIEF","REPORT","EVIDENCE","INVOICE","SPREADSHEET","IMAGE","OTHER"].map(t =>
                      <option key={t}>{t}</option>
                    )}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Parent Thread <span className="text-red-400">*</span></Label>
                  <select value={meta.thread_id} onChange={e => upd("thread_id", e.target.value)}
                    className="w-full rounded-lg border border-slate-200 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="doc-thread-select">
                    <option value="">Select thread…</option>
                    {(threads || []).map(t => (
                      <option key={t.id} value={t.id}>{t.thread_name || t.thread_type}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Visibility Override (optional)</Label>
                <select value={meta.visibility_class} onChange={e => upd("visibility_class", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 text-sm px-3 py-2"
                  data-testid="doc-visibility-select">
                  <option value="">Inherit from thread (default)</option>
                  <option value="CUSTOMER_VISIBLE">Customer Visible</option>
                  <option value="INTERNAL">Internal</option>
                  <option value="FINANCE_ONLY">Finance Only</option>
                  <option value="RESTRICTED">Restricted</option>
                </select>
                <p className="text-xs text-slate-400 mt-1">Visibility can only be equal to or more restrictive than the parent thread.</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Description</Label>
                <Textarea value={meta.description} onChange={e => upd("description", e.target.value)} rows={2} placeholder="Brief description of this document" data-testid="doc-description-input" />
              </div>
            </>
          )}
          {step === "file" && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Document record created. Now upload the file.</p>
              <div
                className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-400 transition-colors"
                onClick={() => fileRef.current?.click()}
                data-testid="file-drop-zone">
                <Upload className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm font-medium text-slate-600">{file ? file.name : "Click to select file"}</p>
                <p className="text-xs text-slate-400 mt-1">PDF, DOCX, XLSX, PPTX, CSV, TXT · JPG, PNG, WEBP · Max 50 MB (20 MB for images)</p>
                <input ref={fileRef} type="file" className="hidden" accept={ALLOWED_MIMES.join(",")}
                  onChange={e => handleFileSelect(e.target.files?.[0])} data-testid="file-input" />
              </div>
              {file && (
                <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3">
                  <FileText className="w-4 h-4 text-indigo-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{file.name}</p>
                    <p className="text-xs text-slate-400">{fmtSize(file.size)}</p>
                  </div>
                </div>
              )}
            </div>
          )}
          {step === "uploading" && (
            <div className="space-y-3 py-4 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-500" />
              <p className="text-sm text-slate-600">Uploading and scanning…</p>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="bg-indigo-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-slate-400">{progress}% — Files are scanned for malware before storage</p>
            </div>
          )}
          {step === "done" && (
            <div className="py-4 text-center">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
              <p className="font-medium text-slate-800">Upload complete</p>
              <p className="text-sm text-slate-500 mt-1">The file has been scanned and stored securely.</p>
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2" data-testid="upload-error">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
          {step === "done" ? (
            <Button onClick={onClose} data-testid="upload-done-btn">Done</Button>
          ) : step === "uploading" ? null : (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              {step === "meta" && (
                <Button onClick={handleMetaSubmit} data-testid="doc-next-btn">Next: Upload File</Button>
              )}
              {step === "file" && (
                <Button onClick={handleUpload} disabled={!file} data-testid="doc-upload-btn">
                  <Upload className="w-3.5 h-3.5 mr-1" />Upload & Scan
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Document Detail Panel ─────────────────────────────────────────────────────
function DocumentDetailPanel({ eid, doc, canApprove, canUpload, canDelete, canVisibility, onClose, onRefresh }) {
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(true);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [visForm, setVisForm] = useState({ vis: doc.visibility_class, reason: "", show: false });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    documentApi.getVersions(eid, doc.id).then(r => setVersions(r.data)).catch(() => {}).finally(() => setLoadingVersions(false));
  }, [eid, doc.id]);

  const handleLifecycle = async (target, extra = {}) => {
    setLifecycleLoading(true);
    try {
      await documentApi.transitionLifecycle(eid, doc.id, { target_status: target, ...extra });
      toast.success(`Document ${target.toLowerCase()}`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transition failed");
    } finally { setLifecycleLoading(false); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await documentApi.downloadFile(eid, doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.title || "document";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    } finally { setDownloading(false); }
  };

  const handleVisibility = async () => {
    if (!visForm.reason.trim()) { toast.error("Reason required for visibility change"); return; }
    try {
      await documentApi.updateVisibility(eid, doc.id, { visibility_class: visForm.vis, reason: visForm.reason });
      toast.success("Visibility updated");
      setVisForm(v => ({ ...v, show: false, reason: "" }));
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Visibility update failed");
    }
  };

  const lc = doc.lifecycle_status;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-end z-50" data-testid="document-detail-panel">
      <div className="bg-white w-full max-w-md h-full shadow-2xl overflow-y-auto flex flex-col">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between sticky top-0 bg-white z-10">
          <div className="flex-1 min-w-0 mr-3">
            <h3 className="font-bold text-slate-900 text-base truncate">{doc.title}</h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <LifecycleBadge status={lc} />
              <VisibilityBadge vis={doc.visibility_class} />
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0">✕</button>
        </div>

        <div className="p-5 flex-1 space-y-5">
          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-slate-400 mb-0.5">Type</p>
              <p className="text-slate-700 font-medium">{doc.document_type}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-0.5">Version</p>
              <p className="text-slate-700 font-medium">v{doc.current_version_number || 0}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-0.5">Created</p>
              <p className="text-slate-700">{fmtDate(doc.created_at)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 mb-0.5">Updated</p>
              <p className="text-slate-700">{fmtDate(doc.updated_at)}</p>
            </div>
            {doc.approved_at && (
              <div className="col-span-2">
                <p className="text-xs text-slate-400 mb-0.5">Approved</p>
                <p className="text-emerald-700 text-sm">{fmtDate(doc.approved_at)}</p>
              </div>
            )}
            {doc.rejection_reason && (
              <div className="col-span-2 bg-red-50 rounded-lg p-3 border border-red-100">
                <p className="text-xs text-red-600 font-medium mb-0.5">Rejection reason</p>
                <p className="text-sm text-red-700">{doc.rejection_reason}</p>
              </div>
            )}
          </div>
          {doc.description && (
            <div>
              <p className="text-xs text-slate-400 mb-1">Description</p>
              <p className="text-sm text-slate-700">{doc.description}</p>
            </div>
          )}

          {/* File actions */}
          {doc.has_file && (
            <div className="space-y-2">
              <Button variant="outline" size="sm" className="w-full" onClick={handleDownload} disabled={downloading} data-testid="download-btn">
                {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                {downloading ? "Downloading…" : "Download File"}
              </Button>
            </div>
          )}
          {!doc.has_file && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              No file uploaded yet. Use Upload File to attach a document.
            </div>
          )}

          {/* Lifecycle actions */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Lifecycle Actions</p>
            {lc === "DRAFT" && canUpload && doc.has_file && (
              <Button size="sm" className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={() => handleLifecycle("UNDER_REVIEW")} disabled={lifecycleLoading} data-testid="submit-review-btn">
                Submit for Review
              </Button>
            )}
            {lc === "UNDER_REVIEW" && canApprove && (
              <div className="space-y-2">
                <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleLifecycle("APPROVED")} disabled={lifecycleLoading} data-testid="approve-doc-btn">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve
                </Button>
                {!showReject ? (
                  <Button size="sm" variant="outline" className="w-full border-red-200 text-red-600 hover:bg-red-50" onClick={() => setShowReject(true)} data-testid="reject-doc-btn">
                    <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Textarea value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} rows={2} placeholder="Reason for rejection (required)" data-testid="rejection-reason-input" />
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setShowReject(false)} className="flex-1">Cancel</Button>
                      <Button size="sm" className="flex-1 bg-red-600 text-white hover:bg-red-700" onClick={() => handleLifecycle("REJECTED", { rejection_reason: rejectionReason })} disabled={!rejectionReason.trim()} data-testid="confirm-reject-btn">
                        Confirm Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {lc === "REJECTED" && canUpload && (
              <p className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">Upload a revised file to return to DRAFT.</p>
            )}
            {lc !== "ARCHIVED" && canDelete && (
              <Button size="sm" variant="outline" className="w-full border-slate-200 text-slate-500 hover:bg-slate-50" onClick={() => handleLifecycle("ARCHIVED")} disabled={lifecycleLoading} data-testid="archive-doc-btn">
                <Archive className="w-3.5 h-3.5 mr-1" />Archive
              </Button>
            )}
          </div>

          {/* Visibility change */}
          {canVisibility && (
            <div className="border border-slate-100 rounded-xl p-4 space-y-2">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1">
                <Shield className="w-3 h-3" />Visibility Override
              </p>
              {!visForm.show ? (
                <Button size="sm" variant="outline" className="w-full" onClick={() => setVisForm(v => ({ ...v, show: true }))} data-testid="change-visibility-btn">
                  Change Visibility
                </Button>
              ) : (
                <div className="space-y-2">
                  <select value={visForm.vis} onChange={e => setVisForm(v => ({ ...v, vis: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 text-sm px-3 py-2" data-testid="visibility-select">
                    <option value="CUSTOMER_VISIBLE">Customer Visible</option>
                    <option value="INTERNAL">Internal</option>
                    <option value="FINANCE_ONLY">Finance Only</option>
                    <option value="RESTRICTED">Restricted</option>
                  </select>
                  <Textarea value={visForm.reason} onChange={e => setVisForm(v => ({ ...v, reason: e.target.value }))} rows={2} placeholder="Reason for visibility change (required)" data-testid="visibility-reason-input" />
                  <p className="text-xs text-amber-600">Visibility can only be made more restrictive — never wider than the parent thread.</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => setVisForm(v => ({ ...v, show: false }))}>Cancel</Button>
                    <Button size="sm" className="flex-1" onClick={handleVisibility} disabled={!visForm.reason.trim()} data-testid="confirm-visibility-btn">Update</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Version history */}
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1 mb-2">
              <History className="w-3 h-3" />Version History
            </p>
            {loadingVersions ? (
              <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-slate-300" /></div>
            ) : versions.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No versions uploaded yet</p>
            ) : (
              <div className="space-y-2" data-testid="version-history-list">
                {versions.map(v => (
                  <div key={v.id} className="bg-slate-50 rounded-lg p-3 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-700">v{v.version_number}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${v.scan_status === "CLEAN" ? "bg-emerald-100 text-emerald-600" : v.scan_status === "QUARANTINED" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-600"}`}>
                        {v.scan_status}
                      </span>
                    </div>
                    <p className="text-slate-500 mt-0.5 truncate">{v.original_filename}</p>
                    <p className="text-slate-400 mt-0.5">{fmtSize(v.file_size)} · {fmtDate(v.uploaded_at)}</p>
                    {v.checksum_sha256 && (
                      <p className="text-slate-300 font-mono mt-0.5 text-[10px]">SHA256: {v.checksum_sha256.slice(0, 16)}…</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Workspace ─────────────────────────────────────────────────────────────
export default function EventDocumentWorkspace({ eid: propEid, event: propEvent, embedded = false }) {
  const { eid: paramEid } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const eid = propEid || paramEid;

  const [docs, setDocs] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");

  // DCC-04B: deep-link target — notification deep links point to ?doc={doc_id}
  // After docs load we'll auto-open the linked document if the ID is present.
  const deepLinkedDocId = searchParams.get("doc");

  const canUpload   = hasCapability(user, "document.upload");
  const canApprove  = hasCapability(user, "document.approve");
  const canDelete   = hasCapability(user, "document.delete");
  const canVisibility = hasCapability(user, "document.visibility.manage");

  const loadDocs = useCallback(async () => {
    try {
      const [docsRes, threadsRes] = await Promise.all([
        documentApi.listDocuments(eid, {
          lifecycle_status: filterStatus || undefined,
          document_type: filterType || undefined,
        }),
        // Use the correct thread endpoint: /api/events/{eid}/threads
        // Response shape: { threads: [...], count: N } or fallback array
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${eid}/threads`, {
          credentials: "include",
        }).then(r => r.json()).catch(() => ({ threads: [] })),
      ]);
      const loadedDocs = docsRes.data || [];
      setDocs(loadedDocs);
      // Handle both {threads: [...]} shape and fallback plain array
      const rawThreads = threadsRes?.threads ?? (Array.isArray(threadsRes) ? threadsRes : []);
      setThreads(rawThreads);

      // DCC-04B: Auto-open the document targeted by a notification deep link.
      // The doc must belong to this event (already scoped by listDocuments(eid, ...))
      // so no additional tenant/event check is needed here — the API enforces it.
      if (deepLinkedDocId && !selectedDoc) {
        const target = loadedDocs.find(d => d.id === deepLinkedDocId);
        if (target) {
          setSelectedDoc(target);
        } else {
          toast.warning("The linked document was not found or you may not have access to it.");
        }
      }
    } catch (e) {
      if (!e.response || e.response.status !== 404) {
        toast.error("Failed to load documents");
      }
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [eid, filterStatus, filterType]); // eslint-disable-line react-hooks/exhaustive-deps
  // Note: deepLinkedDocId and selectedDoc intentionally excluded — auto-open runs once on first load only.

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const filtered = docs.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (d.title || "").toLowerCase().includes(q) ||
           (d.document_type || "").toLowerCase().includes(q);
  });

  // Summary counts
  const totalDocs = docs.length;
  const approvedCount = docs.filter(d => d.lifecycle_status === "APPROVED").length;
  const reviewCount = docs.filter(d => d.lifecycle_status === "UNDER_REVIEW").length;
  const draftCount = docs.filter(d => d.lifecycle_status === "DRAFT").length;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "min-h-screen bg-slate-50"} data-testid="document-workspace">
      {/* Header — standalone mode only */}
      {!embedded && (
        <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-3">
            <button onClick={() => navigate(`/crm/events/${eid}`)} className="text-slate-400 hover:text-slate-700" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <FolderOpen className="w-4 h-4 text-indigo-500" />
            <h1 className="font-bold text-slate-900 text-lg">Documents</h1>
          </div>
        </div>
      )}

      <div className={embedded ? "space-y-5" : "max-w-7xl mx-auto px-6 py-6 space-y-5"}>
        {/* Summary tiles */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "Total", value: totalDocs, icon: FolderOpen, cls: "text-slate-700" },
            { label: "Approved", value: approvedCount, icon: FileCheck, cls: "text-emerald-600" },
            { label: "In Review", value: reviewCount, icon: Clock, cls: "text-amber-600" },
            { label: "Drafts", value: draftCount, icon: FileBadge, cls: "text-slate-500" },
          ].map(({ label, value, icon: Icon, cls }) => (
            <div key={label} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`doc-summary-${label.toLowerCase()}`}>
              <Icon className={`w-5 h-5 ${cls}`} />
              <div>
                <p className="text-2xl font-bold text-slate-900">{value}</p>
                <p className="text-xs text-slate-400">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-48">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search documents…" className="pl-9"
              data-testid="doc-search-input" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="rounded-lg border border-slate-200 text-sm px-3 py-2 text-slate-600"
            data-testid="doc-filter-status">
            <option value="">All Status</option>
            {["DRAFT","UNDER_REVIEW","APPROVED","REJECTED","ARCHIVED"].map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="rounded-lg border border-slate-200 text-sm px-3 py-2 text-slate-600"
            data-testid="doc-filter-type">
            <option value="">All Types</option>
            {["PROPOSAL","CONTRACT","FLOOR_PLAN","DESIGN","PERMIT","BRIEF","REPORT","EVIDENCE","INVOICE","SPREADSHEET","IMAGE","OTHER"].map(t => <option key={t}>{t}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={loadDocs} data-testid="doc-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          {canUpload && (
            <Button size="sm" onClick={() => setShowUpload(true)} data-testid="upload-document-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />New Document
            </Button>
          )}
        </div>

        {/* Document list */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-dashed border-slate-200 py-16 text-center" data-testid="doc-empty-state">
            <FolderOpen className="w-10 h-10 mx-auto text-slate-200 mb-3" />
            <p className="text-slate-500 font-medium">No documents found</p>
            <p className="text-xs text-slate-400 mt-1">
              {canUpload ? "Click \"New Document\" to upload the first file for this event." : "No documents have been uploaded for this event yet."}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="document-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Document</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Type</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Visibility</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Version</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">Updated</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(doc => (
                  <tr key={doc.id} className="hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedDoc(doc)} data-testid={`doc-row-${doc.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-slate-800">{doc.title}</p>
                          {doc.description && <p className="text-xs text-slate-400 truncate max-w-48">{doc.description}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{doc.document_type}</td>
                    <td className="px-4 py-3"><LifecycleBadge status={doc.lifecycle_status} /></td>
                    <td className="px-4 py-3"><VisibilityBadge vis={doc.visibility_class} /></td>
                    <td className="px-4 py-3 text-slate-500">
                      {doc.current_version_number > 0 ? `v${doc.current_version_number}` : "—"}
                      {!doc.has_file && <span className="ml-1 text-amber-500 text-xs">(no file)</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-400">{fmtDate(doc.updated_at)}</td>
                    <td className="px-4 py-3">
                      <button className="text-indigo-500 hover:text-indigo-700 text-xs font-medium" data-testid={`doc-view-btn-${doc.id}`}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
              <p className="text-xs text-slate-400">{filtered.length} document{filtered.length !== 1 ? "s" : ""}</p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showUpload && (
        <UploadModal
          eid={eid}
          threads={threads}
          onClose={() => setShowUpload(false)}
          onCreated={() => { setShowUpload(false); loadDocs(); }}
        />
      )}
      {selectedDoc && (
        <DocumentDetailPanel
          eid={eid}
          doc={selectedDoc}
          canApprove={canApprove}
          canUpload={canUpload}
          canDelete={canDelete}
          canVisibility={canVisibility}
          onClose={() => setSelectedDoc(null)}
          onRefresh={() => { setSelectedDoc(null); loadDocs(); }}
        />
      )}
    </div>
  );
}
